/**
 * Phone Input Utility — wraps intl-tel-input for consistent phone fields.
 *
 * Usage:
 *   import { initPhoneInput, destroyPhoneInput, getPhoneNumber } from '../utils/phone-input.js';
 *   const iti = initPhoneInput(inputElement);
 *   // On form submit: getPhoneNumber(iti) → "+32470123456"
 */

import intlTelInput from 'intl-tel-input';
// Eagerly import the utils bundle instead of code-splitting it.
//
// Before: `loadUtils: () => import('intl-tel-input/utils')` emitted utils as
// a separate webpack chunk (e.g. `38.<hash>.js`). The moment `app.bundle.js`
// was rebuilt with a new chunk layout (even a partial/watch rebuild), any
// tab already running the OLD bundle would request the OLD chunk filename
// and get 404 — leaving intl-tel-input without utils, which can't parse +E.164
// into the national display, so the raw value overlaps the separateDialCode
// button. Same risk on real deploys: a user with an open tab after we ship a
// new bundle would hit the same 404.
//
// Inlining utils (static import + `loadUtils` resolving synchronously)
// bakes it into the already-loaded bundle, so there is no async chunk to
// miss. Cost: utils adds ~80 KB to the main bundle (≈30 KB gzipped), which
// is acceptable given that every page using a phone input (company settings,
// customer form, team form, signup) needs it anyway.
//
// Shape note: `attachUtils` in intl-tel-input v27 reads `module.default` off
// the loader's resolved value (see dist/js/intlTelInput.mjs), so we must
// hand it back in module-namespace shape — NOT the default export directly.
import itiUtils from 'intl-tel-input/utils';

const DEFAULT_OPTIONS = {
    initialCountry: 'be',
    countryOrder: ['be', 'fr', 'nl', 'lu', 'de'],
    nationalMode: true,
    formatOnDisplay: true,
    separateDialCode: true,
    countrySearch: true,
    showFlags: true,
    containerClass: 'iti--fullwidth',
    loadUtils: () => Promise.resolve({ default: itiUtils }),
};

var ERROR_MESSAGES = {
    0: 'Invalid phone number.',
    1: 'Invalid country code.',
    2: 'Phone number is too short.',
    3: 'Phone number is too long.',
    4: 'Invalid phone number.',
};

var instances = new WeakMap();
// Remember the padding-left ITI inlined on each input so we can re-apply it
// if HTMX's attribute settling strips it after a boosted swap. See reapplyItiStyling.
var paddingCache = new WeakMap();

/**
 * Initialize intl-tel-input on a phone input element.
 *
 * The visible input keeps its original name and submits the national-format
 * display value (e.g. "2 736 70 58"). The server's `clean_phone` validators
 * normalise that to E.164 for storage, and Django re-renders the submitted
 * value verbatim on validation errors — so the display stays national across
 * full-page loads, HTMX swaps and client-side validation cancellations.
 *
 * We deliberately do NOT pass `hiddenInput` to intl-tel-input: when the
 * returned `phone` name matches the visible input's name, ITI *reuses* the
 * visible input as the hidden input and rewrites its value to E.164 in its
 * form-submit listener (see `#maybeBuildHiddenInputs` / `#initHiddenInputListener`
 * in node_modules/intl-tel-input/dist/js/intlTelInput.mjs). If another submit
 * handler then calls preventDefault (client-side validation, required-field
 * errors, etc.), the visible input is left stuck on E.164, and the next render
 * shows `+32 +3227367058` under the "+32" dial-code badge.
 *
 * @param {HTMLInputElement} input
 * @param {Object} [overrides] - intl-tel-input option overrides
 * @returns {Object} iti instance
 */
export function initPhoneInput(input, overrides) {
    if (!input || instances.has(input)) return instances.get(input);

    var opts = Object.assign({}, DEFAULT_OPTIONS, overrides || {});

    // Tailwind v4 compiles px-* to padding-inline (logical property) on .form-input.
    // intl-tel-input sets padding-left (physical property) via inline style.
    // Logical properties win over physical ones in the cascade, breaking the layout.
    // Fix: swap .form-input for .form-input-phone which uses physical padding.
    if (input.classList.contains('form-input')) {
        input.classList.remove('form-input');
        input.classList.add('form-input-phone');
    }

    var iti = intlTelInput(input, opts);
    instances.set(input, iti);
    // Snapshot the inline padding-left ITI set so reapplyItiStyling() can
    // restore it if HTMX's attribute settling (attributesToSettle: ['class',
    // 'style', ...]) wipes the input's class/style attributes after swap.
    paddingCache.set(input, input.style.paddingLeft || '');

    // Live validation: clear error styling when the user edits or changes country
    function clearItiError() {
        var wrapper = input.closest('.iti');
        if (wrapper) wrapper.classList.remove('iti--error');
        var fieldGroup = input.closest('.field-group');
        if (fieldGroup && fieldGroup.classList.contains('has-error')) {
            fieldGroup.classList.remove('has-error');
            fieldGroup.querySelectorAll('.field-error').forEach(function (el) { el.remove(); });
        }
    }
    input.addEventListener('input', clearItiError);
    input.addEventListener('countrychange', clearItiError);

    return iti;
}

/**
 * Get the full E.164 number from an iti instance.
 */
export function getPhoneNumber(iti) {
    if (!iti) return '';
    return iti.getNumber() || '';
}

/**
 * Check if the current number is valid.
 */
export function isPhoneValid(iti) {
    if (!iti) return false;
    return iti.isValidNumber();
}

/**
 * Get a human-readable validation error message from an iti instance.
 * Uses getValidationError() to return specific messages (too short, too long, etc.).
 * @param {Object} iti - intl-tel-input instance
 * @param {string} [fallback] - fallback message if error code is unknown
 * @returns {string}
 */
export function getValidationMessage(iti) {
    if (!iti) return ERROR_MESSAGES[0];
    var code = iti.getValidationError();
    return ERROR_MESSAGES[code] || ERROR_MESSAGES[0];
}

/**
 * Get the iti instance for a given input element (or null if not initialized).
 */
export function getInstance(input) {
    return instances.get(input) || null;
}

/**
 * Destroy an iti instance and restore the original input.
 */
export function destroyPhoneInput(input) {
    var iti = instances.get(input);
    if (iti) {
        iti.destroy();
        instances.delete(input);
    }
}

/**
 * Initialize all phone inputs within a container.
 * @param {HTMLElement} container
 * @returns {Object[]} array of iti instances
 */
export function initAllPhoneInputs(container) {
    var root = container || document;
    var results = [];
    root.querySelectorAll('input[data-phone-input]').forEach(function (input) {
        results.push(initPhoneInput(input));
    });
    return results;
}

/**
 * Re-apply ITI's visual state (iti__tel-input class, form-input-phone class,
 * inline padding-left) to already-initialised phone inputs.
 *
 * HTMX's `attributesToSettle` feature clones the OLD element's class/style
 * onto the new element on swap, then restores the new element's SERVER-rendered
 * class/style during the settle phase. That settle task runs AFTER our
 * htmx:afterSwap handler has called initPhoneInput(), so it silently strips
 * `iti__tel-input`, `form-input-phone`, and the inline `padding-left: 86px`
 * ITI sets for `separateDialCode`. The `.iti` wrapper + dial-code element
 * survive, but the input itself reverts to the server's plain `form-input`
 * with 10px padding — so the raw E.164 number renders under the "+32" badge.
 *
 * This function is idempotent: call it from htmx:afterSettle to re-assert the
 * classes + inline padding. Only touches inputs we already know about.
 *
 * @param {HTMLElement} [container]
 */
export function reapplyItiStyling(container) {
    var root = container || document;
    root.querySelectorAll('input[data-phone-input]').forEach(function (input) {
        if (!instances.has(input)) return;
        if (!input.classList.contains('iti__tel-input')) {
            input.classList.add('iti__tel-input');
        }
        if (input.classList.contains('form-input')) {
            input.classList.remove('form-input');
            input.classList.add('form-input-phone');
        } else if (!input.classList.contains('form-input-phone')) {
            input.classList.add('form-input-phone');
        }
        var padding = paddingCache.get(input);
        if (padding && !input.style.paddingLeft) {
            input.style.paddingLeft = padding;
        }
    });
}
